import React, { useEffect } from 'react';
import { Box, IconButton, makeStyles, Typography } from '@material-ui/core';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';

import { Card } from '../../ui/card';
import EmptyState from 'src/components/modules/emptyState';
import { RequestStatus } from '../../../components/utility/RequestStatus';

import { warningColor } from 'src/data/colors';
import { Lifecycle } from 'src/state/request';
import { ApiError, PortfolioQuery } from 'src/api';

interface TableContainerProps<T> {
  elements: T[];
  emptyStateDescription: string;
  emptyStateTitle: string;
  getContent: (element: T) => React.ReactNode;
  getPriority?: (element: T) => boolean;
  labelPriority?: string;
  loading?: boolean;
  priorityHandler?: () => void;
  qtyPriority?: number;
  showPriority?: boolean;
  error?: ApiError;
  Icon?: string;
  query?: PortfolioQuery;
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 24,
  },
  table: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '1fr 1fr',

    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  qtyPriority: {
    alignItems: 'center',
    backgroundColor: warningColor,
    borderRadius: 100,
    color: '#fff',
    display: 'flex',
    fontSize: 13,
    justifyContent: 'center',
    marginLeft: 8,
    padding: '4px 10px',
    width: 'fit-content',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: 8,
  },
  icon: {
    color: '#0000008F',
  },
  statusWrapper: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1000,
    width: 'auto',
    padding: 0,
    textAlign: 'center',
    backgroundColor: 'transparent',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#FFFFFFCC',
    zIndex: 999,
  },
}));

const TableContainer = <T,>({
  elements,
  emptyStateDescription,
  emptyStateTitle,
  getContent,
  getPriority,
  labelPriority,
  priorityHandler,
  qtyPriority,
  showPriority,
  loading,
  error,
  Icon,
  query,
}: TableContainerProps<T>) => {
  const classes = useStyles();
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [query]);

  return (
    <div className={classes.container} ref={containerRef}>
      {(loading || error) && (
        <>
          <div className={classes.overlay} />
          <Box className={classes.statusWrapper}>
            <RequestStatus status={error || Lifecycle.LOADING} />
          </Box>
        </>
      )}
      {!!qtyPriority && (
        <Box className={classes.header}>
          <Typography style={{ gridColumn: '1 / -1' }}>{labelPriority}</Typography>
          <Box className={classes.qtyPriority}>{qtyPriority}</Box>
          <IconButton size="small" onClick={priorityHandler}>
            {showPriority ? (
              <ArrowDropUpIcon className={classes.icon} />
            ) : (
              <ArrowDropUpIcon className={classes.icon} style={{ transform: 'rotate(180deg)' }} />
            )}
          </IconButton>
        </Box>
      )}

      <Box className={classes.table}>
        {elements.length ? (
          elements.map((element, index) => (
            <Card key={index} showPriority={getPriority?.(element)}>
              {getContent(element)}
            </Card>
          ))
        ) : (
          <div style={{ gridColumn: '1 / -1' }}>
            <EmptyState title={emptyStateTitle} description={emptyStateDescription} Icon={Icon} />
          </div>
        )}
      </Box>
    </div>
  );
};

export default TableContainer;
